const post = {
  slug: 'aws-iam-the-hardest-easy-thing',
  number: 18,
  title: 'IAM Is the Hardest Easy Thing in AWS',
  date: '2026-05-24',
  readTime: '7 min read',
  views: 85,
  category: 'aws',
  tags: ['AWS', 'IAM', 'Security'],
  excerpt:
    'Nobody is excited about IAM, and almost every serious AWS problem touches it. A practical mental model for the part of AWS that quietly runs, or quietly breaks, everything.',
  cover: null,
  content: `
Nobody picks AWS because they are excited about permissions. And yet, if I look back at the genuinely serious problems I have had on AWS, IAM is underneath most of them. A deployment fails for no obvious reason. A service cannot reach another service. A security review flags something alarming. A breach post-mortem traces back to one over-permissioned role. The system that controls who can do what turns out to be the system everything else depends on, and it has a reputation for being fiddly mostly because people approach it without a clear mental model.

So here is the model I wish I had been handed early, because once IAM stops feeling like a maze and starts feeling like a few simple rules, it becomes the part of AWS you quietly rely on instead of the part you dread.

## Everything is a policy

At its core, IAM is about policies. A policy is a document that says which actions are allowed on which resources. Identities, like users and roles, get policies attached, and when an identity tries to do something, AWS checks the policies to decide whether to allow it.

\`\`\`json
{
  "Effect": "Allow",
  "Action": ["s3:GetObject"],
  "Resource": "arn:aws:s3:::my-bucket/*"
}
\`\`\`

That is the whole shape of it. An effect, an action, a resource. Once you see that every permission decision is just AWS reading documents like this and answering yes or no, the system stops feeling magical and starts feeling mechanical, which is exactly what you want from the thing guarding your infrastructure.

## Roles, not long-lived keys

The most important habit in AWS security is to stop handing out long-lived access keys. A key in a config file is a key that gets committed to a repo, copied into a chat, or left on an old laptop, and it does not expire on its own. It just sits there, valid, until someone finds it.

The better model is roles. A role is a set of permissions that something can assume temporarily, receiving short-lived credentials that expire automatically. Your application running on AWS should assume a role rather than carry a key. The difference is the difference between a hotel key card that stops working at checkout and a copy of the master key floating around indefinitely with no idea who holds it.

## Least privilege, and mean it

The advice to grant least privilege is everywhere, and it is widely ignored because broad permissions are easier in the moment. When something does not work, the fastest fix is to grant more access until it does, and that wildcard tends to become permanent because nobody comes back to tighten it.

The discipline is to grant exactly the actions a thing needs on exactly the resources it touches, and nothing more. It takes longer up front, and it is the single biggest thing standing between a small mistake and a large breach. When an identity with narrow permissions is compromised, the blast radius is small. When the over-permissioned one is, it is not, and the difference is decided entirely by choices you made while setting it up.

## Read the error, it is telling the truth

IAM errors look intimidating, and they are unusually honest. An access-denied message will generally tell you which action was denied on which resource. That is precisely the information you need, because it points straight at the missing permission.

\`\`\`text
"User is not authorized to perform: s3:PutObject on resource: my-bucket/report.csv"
            -> grant exactly s3:PutObject on exactly that resource, nothing broader
\`\`\`

So resist the urge to respond to a denial by granting broad access to make it go away. Read what was actually denied, grant that one specific thing, and you keep least privilege intact while still solving the problem in front of you.

## A checklist

What I want to be true of IAM on any system I run:

- Permissions expressed as explicit policies: action, resource, effect.
- Workloads assume roles for short-lived credentials, no long-lived keys in configs.
- Each identity granted only the actions and resources it actually needs.
- Denials resolved by granting the specific missing permission, never a wildcard.
- A periodic review that removes access nobody is using anymore.

## The reframe

IAM is not really hard, it is just unforgiving of vagueness. Treat it as a set of explicit documents that say who can do what, prefer temporary roles over permanent keys, grant only what is needed, and read the denials literally instead of papering over them. Do that, and the part of AWS everyone dreads becomes the part that quietly keeps you safe while you get on with everything else.
`,
};

export default post;
